import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Footer from "src/components/Footer";
import Grid from "@mui/material/Grid";
import SoftBox from "src/components/SoftBox";
import SoftTypography from "src/components/SoftTypography";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import * as yup from "yup";
import SoftInput from "src/components/SoftInput";
import SoftButton from "src/components/SoftButton";
import CurrencyFormat from "react-currency-format";
import { ArrowBack } from "@mui/icons-material";
import logo from "src/assets/images/logo-inline.svg";
import toastr from "toastr";
import { capturePaymentIntent } from "src/services/payments.api";

function RenderInput({
  name,
  label,
  errors,
  control,
  fullWidth = false,
  type = "text",
  disabled = false,
}) {
  return (
    <Grid item xs={12} md={fullWidth ? 12 : 6}>
      <SoftBox mb={0.1} lineHeight={0}>
        <SoftBox mb={0.5} ml={0.5}>
          <SoftTypography component="label" variant="caption" fontWeight="bold">
            {label}
          </SoftTypography>
        </SoftBox>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value, ref: inputRef } }) => (
            <SoftInput
              onChange={onChange}
              inputRef={inputRef}
              value={value}
              disabled={disabled}
              type={type}
            />
          )}
        />
        {errors?.[name]?.message && (
          <SoftTypography marginTop={1} fontSize={12} color="error">
            {errors[name].message}
          </SoftTypography>
        )}
      </SoftBox>
    </Grid>
  );
}

export default function InvoicePaymentDetails({
  paymentRequest,
  paymentIntent,
  fees,
}) {
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentToken, setPaymentToken] = useState(null);

  const setupForm = async () => {
    const paymentForm = await window.Forward.createPaymentElement({
      apiKey: import.meta.env.VITE_FWD_API_KEY,
      clientSecret: paymentIntent.client_secret,
    });
    paymentForm.mount("#payment-form", {
      onReady: (tokenize) => {
        tokenize();
      },
      onSuccess: async ({ token }) => {
        setPaymentToken(token);
      },
      onError: (error) => {
        console.error(error);
        setPaymentError(error);
      },
    });
  };

  useEffect(() => {
    setupForm();
  }, [paymentIntent]);

  const schema = yup
    .object({
      password: yup.string().when("createAccount", {
        is: true,
        then: yup
          .string()
          .min(6, "Password must be at least 6 characters")
          .required("A password is required."),
      }),
    })
    .required();

  const { handleSubmit } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const onSubmit = async () => {
    setIsSubmitting(true);
    console.log("Capturing payment request", paymentRequest);
    const paymentResult = await capturePaymentIntent({
      userId: paymentRequest.userId,
      clubId: paymentRequest.clubId,
      locationId: paymentRequest.locationId,
      paymentMethodId: paymentToken,
      paymentIntentId: paymentIntent.id,
      existingPaymentId: paymentRequest.id,
    }).catch((error) => {
      console.error("Error completing payment request", error);
      toastr.error("Error completing payment request", "Payment Error");
      setIsSubmitting(false);
      setPaymentError(error);
    });
    console.log("Payment result", paymentResult);
    if (
      paymentResult &&
      (paymentResult.status === "captured" ||
        paymentResult.status === "uncaptured")
    ) {
      navigate(
        `/pay/${paymentRequest.clubId}/${paymentRequest.id}/confirmation`,
        {
          replace: true,
        }
      );
    } else {
      console.error("Payment Error:", paymentResult);
      toastr.error(
        "Error completing payment request. Please try again.",
        "Payment Error"
      );
    }
    setIsSubmitting(false);
  };

  return (
    <SoftBox
      display="flex"
      flexDirection="column"
      alignItems="center"
      component="form"
      role="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <SoftButton
        onClick={() => navigate(-1)}
        sx={{ left: 0, top: 1, position: "absolute" }}
        color="dark"
        variant="button"
      >
        <ArrowBack fontSize="medium" lineHeight={2} marginRight={2} />

        <SoftTypography variant="h6" fontWeight="light">
          &nbsp;&nbsp;&nbsp;Back
        </SoftTypography>
      </SoftButton>
      <Grid container spacing={3} width={{ sm: 1, md: 0.8 }} pt={5}>
        <Grid item md={12}>
          <SoftBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            pb={3}
          >
            <SoftBox component="img" src={logo} width={250} />
          </SoftBox>
          <SoftTypography variant="h3" p={2} alignSelf="flex-start">
            Checkout/Payment
          </SoftTypography>
        </Grid>
        <Grid item md={6} xs={12}>
          <SoftBox bgColor="white" borderRadius="12px" m={2} p={2}>
            <SoftBox mt={2} mb={2}>
              <SoftTypography mb={1} variant="h5">
                Your Order
              </SoftTypography>
              <hr />
            </SoftBox>

            <SoftBox mb={2}>
              <SoftTypography variant="h6" fontWeight="medium">
                Cost Summary
              </SoftTypography>
            </SoftBox>
            {fees.clubPaysFees ? null : (
              <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                <SoftBox>
                  <SoftTypography
                    variant="button"
                    fontWeight="regular"
                    // color={transaction.clubPaysFees ? 'error' : 'text'}
                  >
                    Taxes & Fees:
                  </SoftTypography>
                </SoftBox>
                <SoftBox ml={1}>
                  <SoftTypography
                    variant="body2"
                    fontWeight="medium"
                    // color={transaction.clubPaysFees ? 'error' : 'text'}
                  >
                    <CurrencyFormat
                      value={(fees.pilotFee ?? 0) / 100}
                      displayType="text"
                      decimalScale={2}
                      fixedDecimalScale
                      thousandSeparator
                      prefix="$"
                    />
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            )}
            <SoftBox display="flex" justifyContent="space-between" mt={3}>
              <SoftBox>
                <SoftTypography variant="body1" fontWeight="light" color="text">
                  Total:
                </SoftTypography>
              </SoftBox>
              <SoftBox ml={1}>
                <SoftTypography variant="body1" fontWeight="medium">
                  <CurrencyFormat
                    value={(fees?.totalCharge ?? 0) / 100}
                    displayType="text"
                    decimalScale={2}
                    fixedDecimalScale
                    thousandSeparator
                    prefix="$"
                  />
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Grid>
        <Grid item md={6} xs={12}>
          <SoftBox bgColor="white" borderRadius="12px" m={2} p={2}>
            <SoftBox mx={4} my={2}>
              <div id="payment-form"></div>
              {paymentError && (
                <SoftTypography marginTop={1} fontSize={14} color="error">
                  {paymentError}
                </SoftTypography>
              )}
              <SoftBox display="flex" flexDirection="row-reverse" mt={4}>
                <SoftButton
                  type="submit"
                  disabled={isSubmitting || !paymentToken}
                  color="primary"
                >
                  Proceed with payment
                </SoftButton>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Grid>
      </Grid>
      <SoftBox display="flex" justifyContent="center">
        <SoftBox width={450}>
          <Footer />
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

RenderInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  errors: PropTypes.object.isRequired,
  control: PropTypes.object.isRequired,
  fullWidth: PropTypes.bool,
  type: PropTypes.string,
  disabled: PropTypes.bool,
};

InvoicePaymentDetails.propTypes = {
  stripe: PropTypes.object,
  elements: PropTypes.object,
  paymentRequest: PropTypes.object,
  clubTransaction: PropTypes.object,
  paymentIntent: PropTypes.object.isRequired,
  paymentMethod: PropTypes.string.isRequired,
};
